import Add from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Link,
  Paper,
  Slider,
  ToggleButton,
  Typography,
} from '@mui/material';
import {
  type ComponentProps,
  type ElementType,
  type FC,
  useRef,
  useState,
} from 'react';
import Markdown, {ExtraProps} from 'react-markdown';
import {ConfidenceLegend} from './Legend/ConfidenceLegend.tsx';
import {ContinuousLegend} from './Legend/ContinuousLegend.tsx';
import {SuperResChip} from '@src/components/visualize/layers/SuperResChip.tsx';
import {SUPER_RES_LAYER_ID} from '@src/constants/analyze.ts';
import useVisualizationParams from '@src/hooks/useVisualizationParams';
import {Layer} from '@src/types/configurations';

const CONFIDENCE_THRESHOLD = 0.5;

type LayerAccordionOnClick =
  | ((layerId: string | undefined, e: React.MouseEvent) => void)
  | (() => void);

type LayerAccordionItemProps = {
  onClick: LayerAccordionOnClick;
  layer: Layer;
  isSelected?: boolean;
};

const heading: ElementType<ComponentProps<'h1'> & ExtraProps> = ({
  node,
  ...rest
}) => {
  return <Typography variant="h6" {...rest} />;
};

const LayerDescription: FC<{children: string}> = ({children}) => {
  return (
    <Markdown
      components={{
        h1: heading,
        h2: heading,
        h3: heading,
        h4: heading,
        h5: heading,
        h6: heading,
        a({node, ...rest}) {
          return <Link target="_blank" rel="noopener noreferrer" {...rest} />;
        },
      }}
    >
      {children}
    </Markdown>
  );
};

export const LayerAccordionItem = ({
  onClick,
  layer,
  isSelected,
}: LayerAccordionItemProps) => {
  const isSuperResLayer = layer?.id === SUPER_RES_LAYER_ID;
  const [isExpanded, setIsExpanded] = useState(isSuperResLayer);
  const {confidenceThreshold, superResOpacity, setSuperResParams} =
    useVisualizationParams();
  const previousConfidenceThreshold = useRef(confidenceThreshold);
  const showConfidence = confidenceThreshold !== null;
  const legend = layer?.styles?.[0]?.legend;

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleConfidenceToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    let confidenceThreshold = showConfidence ? null : CONFIDENCE_THRESHOLD;
    if (!showConfidence && previousConfidenceThreshold.current !== null) {
      confidenceThreshold = previousConfidenceThreshold.current;
    }
    setSuperResParams({confidenceThreshold});
  };

  const handleConfidenceThresholdChange = (_event: Event, newValue: number) => {
    const threshold = newValue;
    setSuperResParams({confidenceThreshold: threshold});
    previousConfidenceThreshold.current = threshold;
  };

  const handleLayerOpacityChange = (_event: Event, newValue: number) => {
    const opacity = newValue;
    setSuperResParams({superResOpacity: opacity});
  };

  const handleRemoveSuperRes = () => {
    setSuperResParams(null);
  };

  return (
    <Accordion
      elevation={isSelected ? 4 : 0}
      sx={{
        '&.Mui-expanded': {
          marginInline: 0,
          marginBlock: 0,
          '&:before': {
            opacity: 1,
          },
        },
      }}
      expanded={(isSelected || isSuperResLayer) && isExpanded}
    >
      <AccordionSummary
        sx={{
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
          },
        }}
        onClick={e => onClick(layer?.id, e)}
      >
        <Paper
          elevation={8}
          sx={{
            boxShadow: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            inlineSize: 36,
            blockSize: 36,
            borderRadius: '50%',
            marginInlineEnd: 2,
            fontSize: 15,
            borderWidth: isSelected ? 2 : 0,
            borderStyle: 'solid',
            borderColor: 'primary.main',
          }}
        >
          {layer?.title.charAt(0) ?? <Add color="action" />}
        </Paper>
        <Typography
          sx={{
            fontSize: 14,
            color: isSelected ? 'text.primary' : 'text.secondary',
          }}
        >
          {layer?.title ?? 'Create custom visualization'}
          {isSuperResLayer && (
            <Box
              sx={{
                display: 'flex',
                fontSize: 8,
                typography: 'caption2',
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              Temporary layer
            </Box>
          )}
        </Typography>
        {((isSelected && Boolean(layer?.description)) || isSuperResLayer) && (
          <ExpandMoreIcon
            sx={theme => ({
              display: !layer ? 'none' : 'block',
              color: isSelected ? 'text.primary' : 'text.secondary',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: theme.transitions.create('transform', {
                duration: theme.transitions.duration.shorter,
                easing: theme.transitions.easing.easeInOut,
              }),
              marginInline: 1,
            })}
            onClick={handleExpandClick}
          />
        )}
        <SuperResChip layer={layer} />
      </AccordionSummary>

      <AccordionDetails
        sx={{
          typography: 'body2',
          color: 'text.secondary',
          paddingInlineStart: '65px',
          paddingBlockStart: 0,
        }}
      >
        {isSuperResLayer && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                marginInlineStart: 'auto',
                marginBlockEnd: 1,
              }}
            >
              <Box sx={{fontSize: 8}} id="super-res-opacity-label">
                Opacity:
              </Box>
              <Slider
                value={superResOpacity ?? 1}
                onChange={handleLayerOpacityChange}
                min={0}
                max={1}
                step={0.01}
                size="small"
                sx={{flexGrow: 1}}
                aria-labelledby="super-res-opacity-label"
              />
            </Box>
            <Divider sx={{marginBlock: 0, borderStyle: 'dashed'}} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                marginInlineStart: 'auto',
                marginBlockStart: 1,
                marginBlockEnd: 1,
              }}
            >
              <Box sx={{whiteSpace: 'nowrap', typography: 'caption'}}>
                Confidence Layer:
              </Box>
              <ToggleButton
                value="confidence-toggle"
                size="small"
                onClick={handleConfidenceToggle}
                aria-label={
                  showConfidence ? 'Hide confidence' : 'Show confidence'
                }
                sx={theme => ({
                  padding: 0.5,
                  backgroundImage: theme.vars?.overlays[0],
                  backgroundColor: theme.palette.background.paper,
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: '#D9D9D980',
                })}
              >
                {showConfidence ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </ToggleButton>
            </Box>
            <ConfidenceLegend
              rootProps={{
                sx: {opacity: showConfidence ? 1 : 0.2, marginBlockEnd: 0.5},
              }}
            />
            <Slider
              value={
                confidenceThreshold ??
                previousConfidenceThreshold.current ??
                CONFIDENCE_THRESHOLD
              }
              onChange={handleConfidenceThresholdChange}
              min={0}
              max={1}
              step={0.01}
              marks={Array(10)
                .fill(0)
                .map((_, i) => ({value: i * 0.1}))}
              size="small"
              disabled={!showConfidence}
              aria-label="Confidence threshold"
              sx={{flexGrow: 1}}
            />
            <Divider
              sx={{
                marginBlockStart: 1.5,
                marginBlockEnd: 1,
                borderStyle: 'dashed',
              }}
            />
          </>
        )}

        {legend?.type === 'continuous' && <ContinuousLegend legend={legend} />}
        <LayerDescription>{layer?.description ?? ''}</LayerDescription>

        {isSuperResLayer && (
          <Button
            variant="text"
            color="secondary"
            startIcon={<DeleteIcon />}
            onClick={handleRemoveSuperRes}
            size="small"
          >
            Remove
          </Button>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default LayerAccordionItem;
